/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import styles from '@app/components/pages/PremiumCallbackPage.module.css';
import * as PremiumActionCreators from '@app/actions/PremiumActionCreators';
import {Spinner} from '@app/components/uikit/Spinner';
import {useLocation} from '@app/lib/router/React';
import {Trans} from '@lingui/react/macro';
import {CheckCircleIcon, XCircleIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import {useEffect, useState} from 'react';

type PreapprovalState =
	| {status: 'error'}
	| {status: 'expired'}
	| {status: 'pending'}
	| {status: 'redirecting'}
	| {status: 'rejected'; actualCountry: string | null; reason: string};

const PremiumCallbackPage = observer(() => {
	const location = useLocation();
	const queryParams = new URLSearchParams(location.search);
	const status = queryParams.get('status');
	const token = queryParams.get('token');
	const [preapprovalState, setPreapprovalState] = useState<PreapprovalState>({status: 'pending'});

	useEffect(() => {
		if (status !== 'preapproval-success') {
			return;
		}

		if (!token) {
			setPreapprovalState({status: 'expired'});
			return;
		}

		let cancelled = false;
		let timeoutId: number | null = null;

		const poll = async () => {
			try {
				const result = await PremiumActionCreators.continueLocalizedCardPreapproval(token);
				if (cancelled) {
					return;
				}

				switch (result.status) {
					case 'pending':
						setPreapprovalState({status: 'pending'});
						timeoutId = window.setTimeout(() => {
							void poll();
						}, 1500);
						return;
					case 'ready':
						setPreapprovalState({status: 'redirecting'});
						window.location.assign(result.url);
						return;
					case 'rejected':
						setPreapprovalState({
							status: 'rejected',
							reason: result.reason,
							actualCountry: result.actual_country ?? null,
						});
						return;
					case 'expired':
						setPreapprovalState({status: 'expired'});
						return;
				}
			} catch {
				if (!cancelled) {
					setPreapprovalState({status: 'error'});
				}
			}
		};

		void poll();

		return () => {
			cancelled = true;
			if (timeoutId != null) {
				window.clearTimeout(timeoutId);
			}
		};
	}, [status, token]);

	const isSuccess = status === 'success';
	const isCancel = status === 'cancel';
	const isClosedBillingPortal = status === 'closed-billing-portal';
	const isPreapprovalSuccess = status === 'preapproval-success';
	const isPreapprovalCancel = status === 'preapproval-cancel';
	const isPreapprovalRejected = isPreapprovalSuccess && preapprovalState.status === 'rejected';
	const isPreapprovalError = isPreapprovalSuccess && preapprovalState.status === 'error';
	const isPreapprovalExpired = isPreapprovalSuccess && preapprovalState.status === 'expired';
	const isPreapprovalPending =
		isPreapprovalSuccess && (preapprovalState.status === 'pending' || preapprovalState.status === 'redirecting');

	return (
		<div className={styles.container}>
			{isSuccess && (
				<>
					<CheckCircleIcon className={styles.successIcon} weight="fill" />
					<div className={styles.content}>
						<h1 className={styles.title}>
							<Trans>Payment Successful</Trans>
						</h1>
						<p className={styles.description}>
							<Trans>Great success! You can now close this tab and return to the app.</Trans>
						</p>
					</div>
				</>
			)}

			{isCancel && (
				<>
					<XCircleIcon className={styles.errorIcon} weight="fill" />
					<div className={styles.content}>
						<h1 className={styles.title}>
							<Trans>Payment Canceled</Trans>
						</h1>
						<p className={styles.description}>
							<Trans>Your payment was canceled. You can now close this tab and return to the app.</Trans>
						</p>
					</div>
				</>
			)}

			{isClosedBillingPortal && (
				<>
					<CheckCircleIcon className={styles.successIcon} weight="fill" />
					<div className={styles.content}>
						<h1 className={styles.title}>
							<Trans>All Done</Trans>
						</h1>
						<p className={styles.description}>
							<Trans>You can now close this tab and return to the app.</Trans>
						</p>
					</div>
				</>
			)}

			{isPreapprovalPending && (
				<>
					<Spinner size="large" />
					<div className={styles.content}>
						<h1 className={styles.title}>
							<Trans>Verifying Card</Trans>
						</h1>
						<p className={styles.description}>
							{preapprovalState.status === 'redirecting' ? (
								<Trans>Your card is eligible. Redirecting you to the payment page now.</Trans>
							) : (
								<Trans>Your card is being checked for localized pricing. This usually takes a moment.</Trans>
							)}
						</p>
					</div>
				</>
			)}

			{isPreapprovalRejected && (
				<>
					<XCircleIcon className={styles.errorIcon} weight="fill" />
					<div className={styles.content}>
						<h1 className={styles.title}>
							<Trans>Card Not Eligible</Trans>
						</h1>
						<p className={styles.description}>
							{preapprovalState.reason === 'country_mismatch' ? (
								preapprovalState.actualCountry ? (
									<Trans>
										Stripe reported a card issued in {preapprovalState.actualCountry}. To pay in this localized
										currency, use a card issued in your billing country, choose another payment method, or return to
										Fluxer and switch to the standard price.
									</Trans>
								) : (
									<Trans>
										This card does not match your billing country, so it cannot be used for this localized currency.
										Use a local card, choose another payment method, or return to Fluxer and switch to the standard
										price.
									</Trans>
								)
							) : (
								<Trans>
									Stripe could not verify this card for localized pricing. Use another card or return to Fluxer and
									choose another payment method or the standard price.
								</Trans>
							)}
						</p>
					</div>
				</>
			)}

			{isPreapprovalExpired && (
				<>
					<XCircleIcon className={styles.errorIcon} weight="fill" />
					<div className={styles.content}>
						<h1 className={styles.title}>
							<Trans>Verification Expired</Trans>
						</h1>
						<p className={styles.description}>
							<Trans>This verification session expired. Return to Fluxer and start checkout again.</Trans>
						</p>
					</div>
				</>
			)}

			{isPreapprovalError && (
				<>
					<XCircleIcon className={styles.errorIcon} weight="fill" />
					<div className={styles.content}>
						<h1 className={styles.title}>
							<Trans>Verification Failed</Trans>
						</h1>
						<p className={styles.description}>
							<Trans>We could not confirm your card right now. Refresh this page or return to Fluxer and try again.</Trans>
						</p>
					</div>
				</>
			)}

			{isPreapprovalCancel && (
				<>
					<XCircleIcon className={styles.errorIcon} weight="fill" />
					<div className={styles.content}>
						<h1 className={styles.title}>
							<Trans>Verification Canceled</Trans>
						</h1>
						<p className={styles.description}>
							<Trans>Card verification was canceled. You can close this tab and return to the app.</Trans>
						</p>
					</div>
				</>
			)}

			{!isSuccess &&
				!isCancel &&
				!isClosedBillingPortal &&
				!isPreapprovalSuccess &&
				!isPreapprovalCancel && (
				<div className={styles.content}>
					<h1 className={styles.title}>
						<Trans>Invalid Status</Trans>
					</h1>
					<p className={styles.description}>
						<Trans>An invalid status was provided. You can now close this tab and return to the app.</Trans>
					</p>
				</div>
			)}
		</div>
	);
});

export default PremiumCallbackPage;
